import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  TextField,
  Typography,
  Box,
  Checkbox,
  NativeSelect,
  Card,
  TableContainer,
} from "@mui/material";

import {
  TABLE_HEAD_FUNDALLOCATOR as TABLE_HEAD,
  Tablecell,
  applySortFilter,
  calculateAndFormatNoAbs,
  calculateAndFormatNoAbsInt,
  fourDecimal,
  getComparator,
  oneDecimal,
  twoDecimal,
} from "../../../../../component/Methods";
import ListHead from "../../../../../component/ListHead/AllocatorList";

const TargetAllocation = ({
  custodians,
  sortedData,
  matchedAllocations,
  setMatchedAllocations,
  fundAum,
  fundId,
  setPieDataTarget,
  editableValue,
  editableData,
  setEditableData,
  StrategiesLowAum,
  setStrategyCombined,
  setStrategyLowAum,
  setHasChanges,
  onDoneEditing,
  strategies,
  StrategiesCombined,
}) => {
  const theme = useTheme();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("allocation");
  const [selectedRows, setSelectedRows] = useState([]);
  const [focusedInput, setFocusedInput] = useState(null);
  const changeSourceRef = useRef(null);

  useEffect(() => {
    const filteredUsers = applySortFilter(
      matchedAllocations,
      getComparator(order, orderBy)
    );
    setEditableData(filteredUsers);
  }, [order, orderBy, matchedAllocations]);

  useEffect(() => {
    if (onDoneEditing) {
      setSelectedRows([]);
    }
  }, [onDoneEditing]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleEditRow = (index, field, value, source) => {
    if (changeSourceRef.current === source) {
      return;
    }
    changeSourceRef.current = source;

    let targetValue = 0;
    if (typeof value == "string") {
      targetValue = parseFloat(value.replace(/,/g, "")) || 0;
    } else if (typeof value === "number") {
      targetValue = value;
    }

    setEditableData((prevData) => {
      if (!prevData) return prevData;

      const updatedData = [...prevData];

      if (field === "allocation" && fundAum > 0) {
        const totalAllocationValue = (targetValue / fundAum) * 100;
        updatedData[index] = {
          ...updatedData[index],
          [field]: value,
          totalAllocation: totalAllocationValue || "",
        };
      } else if (field === "totalAllocation" && editableValue > 0) {
        const newAllocationValue =
          (parseFloat(editableValue) * parseFloat(targetValue)) / 100;
        updatedData[index] = {
          ...updatedData[index],
          [field]: value,
          allocation: calculateAndFormatNoAbsInt(newAllocationValue) || "",
        };
      } else {
        updatedData[index] = { ...updatedData[index], [field]: value };
      }

      setHasChanges(true);

      changeSourceRef.current = null;

      return updatedData;
    });
  };

  const handleChangeEvent = (index, e) => {
    const newValue = e.target.value;
    const regex = /^-?\d*\.?\d*$/;

    if (regex.test(newValue)) {
      handleEditRow(index, "percentShort", newValue, "percentShort");
    } else if (newValue === "" || newValue === "-") {
      handleEditRow(index, "value", newValue);
      handleEditRow(index, "percentShort", newValue, "percentShort");
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(editableData.map((row) => row.name));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (event, rowName) => {
    if (event.target.checked) {
      setSelectedRows([...selectedRows, rowName]);
    } else {
      setSelectedRows(selectedRows.filter((name) => name !== rowName));
    }
  };

  const handleBulkBrokerChange = (value) => {
    setEditableData((prevData) => {
      return prevData.map((row) => {
        if (selectedRows.includes(row.name)) {
          return { ...row, broker: value };
        }
        return row;
      });
    });
    setHasChanges(true);
  };

  const handleBulkAccountChange = (value) => {
    setEditableData((prevData) => {
      return prevData.map((row) => {
        if (selectedRows.includes(row.name)) {
          return { ...row, account: value };
        }
        return row;
      });
    });
    setHasChanges(true);
  };

  return (
    <>
      <Box
        sx={{
          border: `1px ${theme.palette.lightGrey.tableHeader}`,
          borderRadius: "4px",
          width: "100%",
          height: "500px",
          marginRight: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ height: "100%", overflow: "auto" }}>
          <Table size="small" stickyHeader aria-label="a dense table">
            <ListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              onRequestSort={handleRequestSort}
              matchedAllocations={matchedAllocations}
              setMatchedAllocations={setMatchedAllocations}
              custodians={custodians}
              sortedData={sortedData}
              editableValue={editableValue}
              setEditableData={setEditableData}
              editableData={editableData}
              StrategiesLowAum={StrategiesLowAum}
              setStrategyCombined={setStrategyCombined}
              setStrategyLowAum={setStrategyLowAum}
              setHasChanges={setHasChanges}
              fundId={fundId}
              selectedRows={selectedRows}
              handleSelectAll={handleSelectAll}
              handleBulkBrokerChange={handleBulkBrokerChange}
              handleBulkAccountChange={handleBulkAccountChange}
              strategies={strategies}
              StrategiesCombined={StrategiesCombined}
            />
            <TableBody>
              {editableData && editableData.length > 0 ? (
                editableData?.map((row, index) => {
                  return (
                    <TableRow
                      key={index}
                      tabIndex={-1}
                      role="checkbox"
                      sx={{
                        "&:hover": {
                          background: theme.palette.grey[200],
                          cursor: "pointer",
                        },
                      }}
                    >
                      <Tablecell
                        align="left"
                        sx={{
                          paddingLeft: "5px",
                          paddingRight: "5px",
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "5px",
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedRows.includes(row.name)}
                            onChange={(e) => handleRowSelect(e, row.name)}
                            sx={{
                              "& .MuiSvgIcon-root": { fontSize: 16 },
                              padding: "0px",
                              
                            }}
                          />
                          <span style={{ color: theme.palette.text.primary }}>
                            {row?.name}
                          </span>
                          <div
                            style={{
                              height: "10px",
                              width: "10px",
                              background: row?.color,
                            }}
                          ></div>{" "}
                        </div>
                      </Tablecell>
                      <Tablecell
                        align="right"
                        sx={{
                          paddingRight: "5px",
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.existingPercent
                          ? oneDecimal(row.existingPercent)
                          : "0.0"}
                        %
                      </Tablecell>

                      <Tablecell
                        align="right"
                        sx={{
                          paddingRight: "10px",
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.existingAllocation
                          ? row?.existingAllocation
                          : "0"}
                        <span style={{ marginLeft: "5px" }}>$</span>
                      </Tablecell>
                      <Tablecell
                        align="right"
                        sx={{
                          paddingRight: "5px",
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.long ? oneDecimal(row.long) : "0.0"}%
                      </Tablecell>
                      <Tablecell
                        align="right"
                        sx={{
                          paddingRight: "5px",
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.short ? oneDecimal(row.short) : "0.0"}%
                      </Tablecell>
                      <Tablecell
                        align="left"
                        sx={{
                          paddingLeft: "5px",
                          "& .MuiCheckbox-root": { padding: "0px" },
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "left",
                          }}
                        >
                          <Checkbox
                            defaultChecked
                            checked={row?.ignoreWhenRebalancing}
                            size="small"
                            onChange={(e) =>
                              handleEditRow(
                                index,
                                "ignoreWhenRebalancing",
                                e.target.checked,
                                "ignoreWhenRebalancing"
                              )
                            }
                            sx={{
                              "& .MuiSvgIcon-root": { fontSize: 10 },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              marginLeft: "5px",
                              textTransform: "uppercase",
                              border: "none",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {String(row?.ignoreWhenRebalancing)}
                          </Typography>
                        </Box>
                      </Tablecell>
                      <Tablecell
                        align="right"
                        sx={{
                          paddingRight: "5px",
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <TextField
                          value={
                            row?.allocation !== undefined ? row?.allocation : ""
                          }
                          placeholder="0"
                          onChange={(e) => {
                            const value = e.target.value;
                            
                            // Remove commas and allow numbers with decimal points
                            const cleanValue = value.replace(/,/g, "");
                            
                            // Allow empty string, numbers, and decimal points
                            if (cleanValue === "" || /^\d*\.?\d*$/.test(cleanValue)) {
                              handleEditRow(
                                index,
                                "allocation",
                                cleanValue,
                                "allocation"
                              );
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && !isNaN(value)) {
                              const formattedValue = calculateAndFormatNoAbsInt(
                                parseFloat(value)
                              );
                              handleEditRow(
                                index,
                                "allocation",
                                formattedValue,
                                "allocation"
                              );
                            }
                          }}
                          size="small"
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            style: {
                              fontSize: "12px",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                            },
                          }}
                          inputProps={{
                            step: "any",
                            style: {
                              textAlign: "right",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                            },
                          }}
                        />
                      </Tablecell>
                      <Tablecell
                        align="right"
                        sx={{
                          paddingRight: "5px",
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "right",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                            }}
                          >
                            <TextField
                              value={
                                focusedInput === index
                                  ? row?.totalAllocation !== undefined
                                    ? row?.totalAllocation
                                    : ""
                                  : row?.totalAllocation !== undefined
                                  ? oneDecimal(row?.totalAllocation)
                                  : ""
                              }
                              //remove oneDecimal function when focusing
                              onFocus={(e) => {
                                setFocusedInput(index);
                              }}
                              onBlur={(e) => {
                                setFocusedInput(null);
                                // Keep the current value on blur, or set to 0 if empty
                                const value = e.target.value;
                                if (!value || value === "") {
                                  handleEditRow(
                                    index,
                                    "totalAllocation",
                                    0,
                                    "totalAllocation"
                                  );
                                }
                              }}
                              
                              placeholder="0"
                              onChange={(e) => {
                                const value = e.target.value; 

                                const formattedValue =
                                  value.match(/^\d*(\.\d*)?/);

                                if (
                                  formattedValue &&
                                  !isNaN(formattedValue[0])
                                ) {
                                  handleEditRow(
                                    index,
                                    "totalAllocation",
                                    formattedValue[0],
                                    "totalAllocation"
                                  );
                                }
                              }}
                              size="small"
                              variant="standard"
                              InputProps={{
                                disableUnderline: true,
                                style: {
                                  fontSize: "12px",
                                  paddingTop: "0px",
                                  paddingBottom: "0px",
                                },
                              }}
                              inputProps={{
                                step: "any",
                                style: {
                                  textAlign: "right",
                                  paddingTop: "0px",
                                  paddingBottom: "0px",
                                  MozAppearance: "textfield",
                                  WebkitAppearance: "none",
                                  "&::WebkitOuterSpinButton": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "&::WebkitInnerSpinButton": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Tablecell>
                      <Tablecell
                        align="right"
                        sx={{
                          paddingRight: "5px",
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "right",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                            }}
                          >
                            <TextField
                              value={
                                row?.percentLong !== undefined
                                  ? row?.percentLong
                                  : ""
                              }
                              placeholder="0"
                              onChange={(e) => {
                                const value = e.target.value;

                                const formattedValue =
                                  value.match(/^\d*(\.\d*)?/);

                                if (
                                  formattedValue &&
                                  !isNaN(formattedValue[0])
                                ) {
                                  handleEditRow(
                                    index,
                                    "percentLong",
                                    formattedValue[0],
                                    "percentLong"
                                  );
                                }
                              }}
                              size="small"
                              variant="standard"
                              InputProps={{
                                disableUnderline: true,
                                style: {
                                  fontSize: "12px",
                                  paddingTop: "0px",
                                  paddingBottom: "0px",
                                },
                              }}
                              inputProps={{
                                step: "any",
                                style: {
                                  textAlign: "right",
                                  paddingTop: "0px",
                                  paddingBottom: "0px",
                                  MozAppearance: "textfield",
                                  WebkitAppearance: "none",
                                  "&::WebkitOuterSpinButton": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "&::WebkitInnerSpinButton": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                },
                              }}
                            />
                            {row?.percentLong !== undefined &&
                              row?.percentLong !== null &&
                              row?.percentLong !== "" && (
                                <Box
                                  sx={{
                                    fontSize: "11px",
                                    width: "12px",
                                    height: "15px",
                                    mt: "3px",
                                    mr: 1,
                                  }}
                                >
                                  %
                                </Box>
                              )}
                          </Box>
                        </Box>
                      </Tablecell>
                      <Tablecell
                        align="tight"
                        sx={{
                          paddingRight: "5px",
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "right",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                            }}
                          >
                            <TextField
                              value={row?.percentShort}
                              placeholder="0"
                              onChange={(e) => handleChangeEvent(index, e)}
                              size="small"
                              variant="standard"
                              InputProps={{
                                disableUnderline: true,
                                style: {
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  width: "10ch",
                                  marginRight: "0.5ch",
                                  paddingTop: "0px",
                                  paddingBottom: "0px",
                                },
                              }}
                              inputProps={{
                                type: "number",
                                step: "any",
                                style: {
                                  textAlign: "right",
                                  paddingTop: "0px",
                                  paddingBottom: "0px",
                                  MozAppearance: "textfield",
                                  WebkitAppearance: "none",
                                  "&::WebkitOuterSpinButton": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "&::WebkitInnerSpinButton": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                },
                              }}
                            />
                            {row?.percentShort !== undefined &&
                              row?.percentShort !== null &&
                              row?.percentShort !== "" && (
                                <Box
                                  sx={{
                                    fontSize: "11px",
                                    width: "12px",
                                    height: "15px",
                                    mt: "3px",
                                    mr: 1,
                                  }}
                                >
                                  %
                                </Box>
                              )}
                          </Box>
                        </Box>
                      </Tablecell>
                      <Tablecell
                        align="left"
                        sx={{
                          paddingLeft: "5px",
                          "& .MuiNativeSelect-icon": { display: "none" },
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <NativeSelect
                          value={row.broker}
                          onChange={(e) =>
                            handleEditRow(
                              index,
                              "broker",
                              e.target.value,
                              "broker"
                            )
                          }
                          autoSelect
                          disableUnderline
                          sx={{
                            fontSize: "12px",
                            height: "4px",
                            width: "100%",
                            padding: "0",
                            margin: "0",
                          }}
                          id={`broker-${index}`}
                          name={`broker-${index}`}
                        >
                          {sortedData?.filter(val => val.abbreviation != null).map((val, idx) => (
                            <option
                              key={val.abbreviation || `broker-${idx}`}
                              value={val.abbreviation}
                            >
                              {val.abbreviation}
                            </option>
                          ))}
                        </NativeSelect>
                      </Tablecell>
                      <Tablecell
                        align="left"
                        sx={{
                          paddingLeft: "5px",
                          "& .MuiNativeSelect-icon": { display: "none" },
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <NativeSelect
                          value={row.account}
                          onChange={(e) =>
                            handleEditRow(
                              index,
                              "account",
                              e.target.value,
                              "account"
                            )
                          }
                          autoSelect
                          disableUnderline
                          sx={{
                            fontSize: "12px",
                            height: "4px",
                            width: "100%",
                            padding: "0",
                            margin: "0",
                          }}
                          id={`custodian-${index}`}
                          name={`custodian-${index}`}
                        >
                          {custodians?.filter(val => val.custodian != null).map((val, idx) => (
                            <option key={val.custodian || `custodian-${idx}`} value={val.name}>
                              {val.name}
                            </option>
                          ))}
                        </NativeSelect>
                      </Tablecell>
                      <Tablecell
                        align="left"
                        sx={{
                          paddingLeft: "5px",
                          "& .MuiCheckbox-root": { padding: "0px" },
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "left",
                          }}
                        >
                          <Checkbox
                            defaultChecked
                            checked={row?.byPass}
                            size="small"
                            onChange={(e) =>
                              handleEditRow(
                                index,
                                "byPass",
                                e.target.checked,
                                "byPass"
                              )
                            }
                            sx={{
                              "& .MuiSvgIcon-root": { fontSize: 10 },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              marginLeft: "5px",
                              textTransform: "uppercase",
                              border: "none",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {String(row?.byPass)}
                          </Typography>
                        </Box>
                      </Tablecell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <Tablecell colSpan={13} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No data available
                    </Typography>
                  </Tablecell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default TargetAllocation;
